import { Device, i18n } from '@goodboydigital/astro';
import gsap from 'gsap';
import { Container, Sprite, Text, Texture } from 'pixi.js';
import { SignalConnection } from 'typed-signals';

import { App, Shared } from '../../App';
import { Browser } from '../../utils/Browser';
import { CloseButton } from '../buttons/CloseButton';
import { NineSliceButton } from '../buttons/NineSliceButton';
import { Diamond } from '../Diamond';
import LocalisedText from '../LocalisedText';
import { Panel } from '../Panel';

const defaultOpts = {
    callback: undefined as (() => void) | undefined,
};

export type DownloadPopupOptions = typeof defaultOpts;

export class DownloadPopup
{
    public app: App;
    public view: Container;
    public content: Container;
    public panel: Panel;
    public shade: Sprite;
    public title: Text;
    public info: Text;
    public closeButton: CloseButton;
    public downloadButton: NineSliceButton;
    public presenting = false;
    public options: DownloadPopupOptions;

    private resizeConnection?: SignalConnection;
    private phoneTablet: Container;
    private diamonds: Container;
    private biker: Container;

    constructor(app: App, opts: Partial<DownloadPopupOptions> = {})
    {
        this.app = app;
        this.view = new Container();
        this.options = { ...defaultOpts, ...opts };

        this.shade = new Sprite(Texture.WHITE);
        this.shade.tint = 0x000000;
        this.shade.interactive = true;
        this.shade.anchor.set(0.5);
        this.shade.alpha = 0.9;
        this.view.addChild(this.shade);

        this.content = new Container();
        this.view.addChild(this.content);

        this.panel = new Panel({ w: 800, h: 500 });
        this.content.addChild(this.panel.view);
        this.panel.turnOnShining();

        this.phoneTablet = this.createPhoneTablet();
        this.content.addChild(this.phoneTablet);

        this.title = new LocalisedText('appDownloadPopup_title', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 8,
            lineJoin: 'round',
            align: 'center',
            fontSize: 40,
            lineHeight: 40,
            wordWrap: true,
            wordWrapWidth: 650,
            maxHeight: 100
        });
        this.title.anchor.set(0.5, 0);
        this.title.y = (-this.panel.bg.height / 2) + 50;
        this.content.addChild(this.title);

        this.biker = this.createBiker();
        this.content.addChild(this.biker);

        const infoTranslationId = Device.desktop ? 'appDownloadPopup_search' : 'appDownloadPopup_downloadNow';

        this.info = new LocalisedText(infoTranslationId, {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 8,
            lineJoin: 'round',
            align: 'center',
            fontSize: 36,
            lineHeight: 36,
            wordWrap: true,
            wordWrapWidth: 650,
            maxHeight: 100
        });
        this.info.anchor.set(0.5, 1);
        this.info.y = (this.panel.bg.height / 2) - 50;
        this.content.addChild(this.info);
        if (!Device.desktop) this.info.y -= 40;

        this.diamonds = this.createDiamonds();
        this.content.addChild(this.diamonds);

        this.downloadButton = this.createAppStoreButton();
        this.view.addChild(this.downloadButton.view);

        this.closeButton = new CloseButton({
            startHidden: true,
        });
        this.view.addChild(this.closeButton.view);
        this.closeButton.onPress.connect(() => this.dismiss());

        this.resize();
    }

    public async present(opts?: Partial<DownloadPopupOptions>): Promise<void>
    {
        if (this.presenting) return;
        this.presenting = true;
        this.app.stage.stage.addChild(this.view);
        this.resizeConnection = this.app.resize.onResize.connect(() => this.resize());
        this.resize();

        this.killTweens();

        this.content.scale.set(0.5);
        this.content.alpha = 1;
        this.downloadButton.view.scale.set(0);
        this.closeButton.view.scale.set(0);
        this.shade.alpha = 0.00001;

        Shared.game.soundSystem.playSfx('windmill');

        gsap.to(this.shade, { alpha: 0.75, duration: 0.2 });
        await gsap.to(this.content.scale, { x: 1, y: 1, duration: 0.3, ease: 'back.out' });

        if (opts?.callback) opts?.callback();

        if (!Device.desktop) await this.downloadButton.show();
        await this.closeButton.show();
    }

    public async dismiss(): Promise<void>
    {
        if (!this.presenting) return;

        this.presenting = false;
        this.killTweens();
        if (this.resizeConnection)
        {
            this.resizeConnection.disconnect();
            this.resizeConnection = undefined;
        }

        this.downloadButton.hide();
        this.closeButton.hide();
        gsap.to(this.content.scale, { x: 1.5, y: 1.5, duration: 0.3, ease: 'back.in' });
        gsap.to(this.content, { alpha: 0, duration: 0.3, ease: 'sine.in' });
        await gsap.to(this.shade, { alpha: 0, duration: 0.2, delay: 0.1 });

        if (this.view.parent) this.view.parent.removeChild(this.view);

        if (Shared.game.playing) Shared.game.resume();
    }

    public resize(): void
    {
        this.view.x = this.app.resize.width / 2;
        this.view.y = this.app.resize.height / 2;
        this.shade.width = this.app.resize.width;
        this.shade.height = this.app.resize.height;

        this.downloadButton.view.x = 0;
        this.downloadButton.view.y = (this.panel.bg.height / 2) - 20;

        this.closeButton.view.x = (this.panel.bg.width / 2) - 20;
        this.closeButton.view.y = (-this.panel.bg.height / 2) + 20;
    }

    private killTweens(): void
    {
        gsap.killTweensOf(this.shade);
        gsap.killTweensOf(this.downloadButton.view);
        gsap.killTweensOf(this.closeButton.view);
        gsap.killTweensOf(this.content);
        gsap.killTweensOf(this.content.scale);
    }

    private createPhoneTablet(): Container
    {
        const container = new Container();

        const tablet = new Container();
        const tabletFrame = new Sprite(Shared.assets.getTexture('tablet-frame'));
        const tabletScreenshot = new Sprite(Shared.assets.getTexture('tablet-screenshot'));

        tabletFrame.anchor.set(0.5);
        tabletScreenshot.anchor.set(0.5);
        tablet.addChild(tabletScreenshot);
        tablet.addChild(tabletFrame);
        container.addChild(tablet);
        tablet.x = 60;

        const phone = new Container();
        const phoneShadow = new Sprite(Shared.assets.getTexture('phone-shadow'));
        const phoneFrame = new Sprite(Shared.assets.getTexture('phone-frame'));
        const phoneScreenshot = new Sprite(Shared.assets.getTexture('phone-screenshot'));

        phoneShadow.anchor.set(0.5);
        phoneFrame.anchor.set(0.5);
        phoneScreenshot.anchor.set(0.5);
        phone.addChild(phoneShadow);
        phone.addChild(phoneScreenshot);
        phone.addChild(phoneFrame);
        container.addChild(phone);
        phone.x = -60;
        phone.y = 30;
        phoneShadow.x = 10;
        phoneShadow.y = 10;

        return container;
    }

    private createAppStoreButton(): NineSliceButton
    {
        const container = new Sprite();
        const iconTexture = Device.android ? 'google-play-icon' : 'apple-icon';
        const store = Device.android ? 'playStore' : 'appStore';
        const storeTranslationId = `appDownloadPopup_${store}`;
        const linkId = `link_${store}`;

        const icon = new Sprite(Shared.assets.getTexture(iconTexture));

        icon.anchor.set(0.5);
        container.addChild(icon);
        icon.x = -130;

        const textDownload = new LocalisedText('appDownloadPopup_downloadOn', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 8,
            lineJoin: 'round',
            align: 'center',
            fontSize: 30,
        });

        textDownload.anchor.set(0.5, 1);
        container.addChild(textDownload);
        textDownload.x = 50;

        const textStore = new LocalisedText(storeTranslationId, {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 8,
            lineJoin: 'round',
            align: 'center',
            fontSize: 40,
        });

        textStore.anchor.set(0.5, 0);
        container.addChild(textStore);
        textStore.x = 50;
        textStore.y = -10;

        const button = new NineSliceButton({
            icon: container,
            bgAsset: `button-ninesliced-magenta`,
            w: 450,
            h: 130,
            startHidden: true,
        });

        button.onPress.connect(() =>
        {
            Browser.open(i18n.translate(linkId));
            this.dismiss();
        });

        return button;
    }

    private createDiamonds(): Container
    {
        const container = new Container();

        const diamond1 = container.addChild(new Diamond('small', 1.25, true));
        const diamond2 = container.addChild(new Diamond('small', 1.25, true));
        const diamond3 = container.addChild(new Diamond('small', 1.25, true));
        const diamond4 = container.addChild(new Diamond('small', 1.25, true));
        const diamond5 = container.addChild(new Diamond('small', 1.25, true));

        diamond1.x = 230;
        diamond1.y = -50;
        diamond1.rotation = 0.6;

        diamond2.x = 250;
        diamond2.y = 0;
        diamond2.rotation = 0.2;

        diamond3.x = 180;
        diamond3.y = 80;
        diamond3.rotation = 0.9;

        diamond4.x = -270;
        diamond4.y = -70;
        diamond4.rotation = -0.5;

        diamond5.x = -230;
        diamond5.y = 0;
        diamond5.rotation = 0.5;

        return container;
    }

    private createBiker(): Container
    {
        const container =  Shared.game.garage.getPlainSprite({
            chassis: 'motobike',
            wheelLeft: 'pizza',
            wheelRight: 'bmx',
            character: 'darwin',
            color: '#EAA149',
        });

        container.x = -280;
        container.y = Device.desktop ? 106 : 140;
        container.rotation = -0.6;
        container.scale.set(1.25);

        return container;
    }
}
